// src/components/layout/ChatColumn/CollapsedDocumentCard.jsx
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { FileText, ArrowUpRight } from 'lucide-react';
import { usePanelManager, PanelTypes, CONTENT_TYPES } from '@/managers/PanelManager';

const CollapsedDocumentCard = ({
    content,
    messageId,
    blockIndex,
}) => {
    const { showInPortal, togglePanelWithMemory, isPanelOpen, getPortalContent } = usePanelManager();

    const [isHovered, setIsHovered] = useState(false);
    const portalId = useMemo(() => `doc-${messageId}-${blockIndex}`, [messageId, blockIndex]);

    const portalContent = getPortalContent();
    const isThisBlockCurrentlyInPortal = useMemo(() =>
        isPanelOpen(PanelTypes.PORTAL) &&
        portalContent?.type === CONTENT_TYPES.DOCUMENT &&
        portalContent?.metadata?.portalId === portalId,
        [isPanelOpen, portalContent, portalId]
    );
    
    // Extract a title or create a snippet from the document content
    const { title, snippet } = useMemo(() => {
        const lines = content.split('\n');
        const firstLine = lines[0] || '';
        let extractedTitle = 'Document';
        let extractedSnippet = (lines.slice(0, 5).join('\n') || content.substring(0, 150)).trim();

        // Check for a markdown H1 header as a title
        if (firstLine.startsWith('# ')) {
            extractedTitle = firstLine.substring(2).trim();
            // If we used the H1, the snippet shouldn't include it.
            extractedSnippet = (lines.slice(1, 6).join('\n') || content.substring(firstLine.length).substring(0, 150)).trim();
        }

        if (extractedSnippet.length > 150) {
            extractedSnippet = extractedSnippet.substring(0, 150) + '...';
        }

        return { title: extractedTitle, snippet: extractedSnippet };
    }, [content]);

    const handlePortalOpen = useCallback(async () => {
        await showInPortal({
            type: CONTENT_TYPES.DOCUMENT,
            content: content, // The 'content' prop is already clean from MessageParser
            metadata: {
                messageId,
                blockIndex,
                portalId,
                contentType: CONTENT_TYPES.DOCUMENT,
                title: title
            }
        });
    }, [showInPortal, content, messageId, blockIndex, portalId, title]);

    const handlePortalClose = useCallback(() => {
        if (isThisBlockCurrentlyInPortal) {
            togglePanelWithMemory(PanelTypes.PORTAL);
        }
    }, [togglePanelWithMemory, isThisBlockCurrentlyInPortal]);

    const handleTogglePortal = useCallback(() => {
        if (isThisBlockCurrentlyInPortal) {
            handlePortalClose();
        } else {
            handlePortalOpen();
        }
    }, [isThisBlockCurrentlyInPortal, handlePortalOpen, handlePortalClose]);

    return (
        <div
            onClick={handleTogglePortal}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            className={`w-full group flex flex-col items-start gap-3 px-4 py-3.5 mb-4 last:mb-0 rounded-lg shadow-[0_2px_4px_rgba(0,0,0,0.1)] border transition-all duration-150 ease-in-out cursor-pointer relative ${
                isThisBlockCurrentlyInPortal
                    ? 'border-primary shadow-[0_0_15px_rgba(var(--primary-rgb),0.15)]'
                    : 'border-border-subtle hover:border-surface-dark'
            }`}
            role="button"
            tabIndex={0}
            aria-label={`Document: ${title}. Click to view.`}
        >
            <div className="relative z-10 flex flex-col w-full gap-2.5">
                <div className="flex items-center gap-2 w-full">
                    <div className={`p-1.5 rounded-md transition-colors duration-150 ${
                        isThisBlockCurrentlyInPortal ? 'bg-primary/10 text-primary' : 
                        isHovered ? 'bg-white/10 text-on-surface' : 'bg-white/5 text-on-surface-dim'
                    }`}>
                        <FileText className="w-4 h-4" />
                    </div>
                    
                    <span className={`text-sm font-medium transition-colors duration-150 ${
                        isThisBlockCurrentlyInPortal ? 'text-primary' : 'text-on-surface'
                    }`}>
                        {title}
                    </span>
                    
                    <div className="flex-1"></div>
                    
                    <div className={`flex items-center gap-1 transition-opacity duration-150 ${
                        isHovered || isThisBlockCurrentlyInPortal ? 'opacity-100' : 'opacity-0'
                    }`}>
                        <button
                            onClick={(e) => { e.stopPropagation(); handleTogglePortal(); }}
                            className="p-1 rounded-md text-on-surface-dim hover:text-on-surface hover:bg-white/10 transition-colors"
                            aria-label={isThisBlockCurrentlyInPortal ? "Close in Portal" : "View in Portal"}
                        >
                            <ArrowUpRight className={`w-3.5 h-3.5 transition-transform duration-150 ${isThisBlockCurrentlyInPortal ? 'rotate-45' : ''}`} />
                        </button>
                    </div>
                </div>

                {snippet && (
                    <p className="text-xs text-on-surface-dim leading-relaxed">
                        {snippet}
                    </p>
                )}
            </div>
        </div>
    );
};

CollapsedDocumentCard.propTypes = {
    content: PropTypes.string.isRequired,
    messageId: PropTypes.string.isRequired,
    blockIndex: PropTypes.number.isRequired,
};

export default React.memo(CollapsedDocumentCard);